import React from 'react';
import { convertSrcJss } from '_utils/media';
import { Image } from '@sitecore-jss/sitecore-jss-react';
import Arrow from '_utils/icons/Arrow';
import External from '_utils/icons/External';
import NavLink from '../NavItem';
import { getContentType } from '_utils/helpers/contentType';
import { isContentful, resizeContentfulImage } from '_utils/helpers/image';
import { getLink } from '_utils/helpers/link';
import { imagePlaceholderSrc } from '_utils/styles';
import ContentTypeAttributes from '_utils/components/Corporate/ContentTypeAttributes';
import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';

import { Item as ItemProps } from './definitions';

import {
  ArrowWrapper,
  Card,
  CardHeader,
  CardSection,
  CardFooter,
  Category,
  Title,
  AttributesWrapper,
  LinkIconContainer,
  ImageWrapper,
  ImageContainer
} from './StyledRelatedContent';

const ItemContainer = withEditModeNotice(NavLink);

const renderImage = (field, editMode) => {
  if (editMode) {
    return <Image field={field} />;
  }

  if (field === null || Boolean(field?.value?.src) === false) {
    return null;
  }

  let resizeImage;

  const internal = field?.value?.src?.startsWith('/');

  if (isContentful(field.value.src)) {
    resizeImage = resizeContentfulImage(field?.value?.src, { width: 640 });
  } else if (internal) {
    resizeImage = convertSrcJss(field.value.src) + '?mw=640';
  } else {
    resizeImage = field.value.src;
  }

  return (
    <img
      src={imagePlaceholderSrc}
      data-src={resizeImage}
      alt={field.value.alt}
      className="lazyload"
    />
  );
};

const RelatedItem: React.FC<ItemProps> = ({
  id,
  image,
  link,
  type,
  title,
  primaryCategory,
  attributes,
  valid,
  editMode,
  description
}) => {
  const contentType = getContentType(type);

  let img = renderImage(image, editMode);

  let cardClass = [];

  cardClass.push(contentType);

  cardClass.push(img ? 'image' : 'no-image');

  const linkObject = getLink(link);

  return (
    <ItemContainer
      StyledLink={Card}
      ariaLabelledBy={`title-${id}`}
      link={linkObject}
      className={cardClass.join(' ')}
      noticeEnabled={editMode && !valid}
      noticeLevel="warning"
      noticeMessage={'Link and title not set, item will not display'}
      description={description}
    >
      <CardHeader>
        <ImageWrapper>
          <ImageContainer>{img}</ImageContainer>
        </ImageWrapper>
      </CardHeader>
      <CardSection>
        {primaryCategory && <Category>{primaryCategory}</Category>}
        <Title tag="span" field={{ value: title }} id={`title-${id}`} />
        <ContentTypeAttributes
          attributes={attributes}
          contentType={contentType}
          Wrapper={AttributesWrapper}
        />
      </CardSection>
      <CardFooter>
        {linkObject !== null && (
          <LinkIconContainer>
            <ArrowWrapper>
              {linkObject.type !== 'external' && <Arrow ariaHidden={true} />}
              {linkObject.type === 'external' && <External ariaHidden={true} />}
            </ArrowWrapper>
          </LinkIconContainer>
        )}
      </CardFooter>
    </ItemContainer>
  );
};

export default withEditMode(RelatedItem);
